import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';

interface BasicInfo {
  name: string;
  age: number;
  gender: string;
  sport: string;
  yearsOfExperience: number;
  competitionLevel: string;
  email: string;
}

interface Props {
  onSubmit: (info: BasicInfo) => void;
}

const BasicInfoForm: React.FC<Props> = ({ onSubmit }) => {
  const [formData, setFormData] = useState<BasicInfo>({
    name: '',
    age: 0,
    gender: '',
    sport: '',
    yearsOfExperience: 0,
    competitionLevel: '',
    email: '',
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const validate = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = '氏名を入力してください';
    }
    if (formData.age < 15 || formData.age > 100) {
      newErrors.age = '有効な年齢を入力してください';
    }
    if (!formData.gender) {
      newErrors.gender = '性別を選択してください';
    }
    if (!formData.sport.trim()) {
      newErrors.sport = '競技種目を入力してください';
    }
    if (formData.yearsOfExperience < 0) {
      newErrors.yearsOfExperience = '競技歴を入力してください';
    }
    if (!formData.competitionLevel) {
      newErrors.competitionLevel = '競技レベルを選択してください';
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      newErrors.email = '有効なメールアドレスを入力してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  const handleChange = (field: keyof BasicInfo, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h5" gutterBottom sx={{ mb: 4, fontWeight: 600 }}>
        基本情報の入力
      </Typography>

      <Typography variant="body1" sx={{ mb: 4, color: '#666' }}>
        あなたの基本情報を入力してください。これらの情報は診断結果の精度向上と、企業とのマッチングに活用されます。
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <TextField
          label="氏名"
          required
          fullWidth
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          error={!!errors.name}
          helperText={errors.name}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="年齢"
            required
            type="number"
            value={formData.age || ''}
            onChange={(e) => handleChange('age', parseInt(e.target.value) || 0)}
            error={!!errors.age}
            helperText={errors.age}
            sx={{ flex: 1 }}
          />

          <FormControl sx={{ flex: 1 }} error={!!errors.gender} required>
            <InputLabel>性別</InputLabel>
            <Select
              value={formData.gender}
              label="性別"
              onChange={(e) => handleChange('gender', e.target.value)}
            >
              <MenuItem value="male">男性</MenuItem>
              <MenuItem value="female">女性</MenuItem>
              <MenuItem value="other">その他</MenuItem>
            </Select>
            {errors.gender && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                {errors.gender}
              </Typography>
            )}
          </FormControl>
        </Box>

        <TextField
          label="競技種目"
          required
          fullWidth
          value={formData.sport}
          onChange={(e) => handleChange('sport', e.target.value)}
          error={!!errors.sport}
          helperText={errors.sport}
          placeholder="例: サッカー、陸上競技、水泳など"
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="競技歴（年）"
            required
            type="number"
            value={formData.yearsOfExperience || ''}
            onChange={(e) =>
              handleChange('yearsOfExperience', parseInt(e.target.value) || 0)
            }
            error={!!errors.yearsOfExperience}
            helperText={errors.yearsOfExperience}
            sx={{ flex: 1 }}
          />

          <FormControl
            sx={{ flex: 1 }}
            error={!!errors.competitionLevel}
            required
          >
            <InputLabel>競技レベル</InputLabel>
            <Select
              value={formData.competitionLevel}
              label="競技レベル"
              onChange={(e) => handleChange('competitionLevel', e.target.value)}
            >
              <MenuItem value="international">国際レベル</MenuItem>
              <MenuItem value="national">全国レベル</MenuItem>
              <MenuItem value="regional">地域レベル</MenuItem>
              <MenuItem value="school">学校レベル</MenuItem>
              <MenuItem value="recreational">趣味レベル</MenuItem>
            </Select>
            {errors.competitionLevel && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                {errors.competitionLevel}
              </Typography>
            )}
          </FormControl>
        </Box>

        <TextField
          label="メールアドレス"
          required
          fullWidth
          type="email"
          value={formData.email}
          onChange={(e) => handleChange('email', e.target.value)}
          error={!!errors.email}
          helperText={errors.email || '診断結果をお送りします'}
        />

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            sx={{
              px: 6,
              py: 1.5,
              fontSize: '1rem',
            }}
          >
            次へ
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default BasicInfoForm;
